// Preferred bookings view (booking_page_mode) helpers.
//
// booking_page_mode is a per-user setting (0 = list, 1 = calendar,
// 2 = resource calendar, 3 = kanban) returned by users/site-settings and
// mirrored in localStorage under `profile.booking_page_mode`.
//
// The view-switcher on every bookings page already writes the new mode into
// `profile.booking_page_mode` BEFORE navigating away (see goToCalendar /
// updateBookingPageMode / switchToEventCalendar in the view components). We read
// that SAME field so the /bookings entry redirect and the per-page mount guards
// route to the user's current preference without first mounting a heavy page and
// fetching its full data set, and without fighting the view switcher. Using a
// separate cache key here would go stale the moment a user switched views and
// bounce them straight back — so always read/write `profile.booking_page_mode`.

const BOOKING_PAGE_MODE_PATHS = {
  0: '/bookings/list',
  1: '/bookings/calendar',
  2: '/bookings/resource-calendar',
  3: '/bookings/kanban',
}

function readProfile() {
  try {
    return JSON.parse(localStorage.getItem('profile')) || null
  } catch (e) {
    return null
  }
}

// Refresh the cached preference from a site-settings response so it stays in
// sync with the server on every load. Writes into the existing `profile` object
// (the same field the view switchers maintain). Guards against undefined/null so
// callers can pass response.data.booking_page_mode without checking first.
export function cacheBookingPageMode(mode) {
  if (mode === undefined || mode === null) return
  const profile = readProfile()
  if (!profile) return
  profile.booking_page_mode = mode
  localStorage.setItem('profile', JSON.stringify(profile))
}

// Returns the route path for the user's preferred view, or null when no
// preference is known yet (so callers fall back to /bookings/list).
export function getPreferredBookingsPath() {
  const profile = readProfile()
  const mode = profile ? profile.booking_page_mode : null
  if (mode === undefined || mode === null) return null
  return BOOKING_PAGE_MODE_PATHS[mode] || null
}

// Query params that are per-view filter state (list and calendar each read
// `status`/`game` as THEIR OWN filters on mount, and FilterBar's precedence
// treats URL filters as explicit user intent — above the page's saved views).
// Carrying them across a view switch makes the target view silently inherit
// filters the user set on a different view (e.g. the calendar opening
// booked-only because the list keeps status=booked), and the leaked value can
// then be autosaved into the target page's last-used view. Cross-view
// navigations must strip these while still forwarding everything else
// (onboarding/categoryId/taskId, selected_booking, date, ...).
const VIEW_SCOPED_FILTER_PARAMS = ['status', 'game', 'has_waivers']

export function stripViewFilterParams(query) {
  if (!query) return {}
  const cleaned = { ...query }
  VIEW_SCOPED_FILTER_PARAMS.forEach((key) => delete cleaned[key])
  return cleaned
}

// Booking deep link: /bookings?selected_booking=<slot id>. The /bookings entry
// redirect forwards it to the user's preferred view, and every view opens the
// booking's date with its drawer open. The view lambdas never return cancelled
// or removed slots, so those get no link.
const NOT_ON_CALENDAR_STATUSES = ['cancelled', 'removed']

// 'YYYY-MM-DD' from a date or an ISO timestamp ('2026-09-18T00:00:00.000Z'), or null.
export function toIsoDate(raw) {
  if (!raw || typeof raw !== 'string') return null
  const date = raw.split('T')[0]
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null
}

// Href for a transaction booking row, or null when the slot can't be shown.
// Public bookings link to their master slot, the id the row displays. The row's
// date rides along so the view opens on it without a lookup round trip.
export function bookingCalendarLink(row) {
  if (!row || NOT_ON_CALENDAR_STATUSES.includes(row.status)) return null
  const id = row.master_slot_id || row.id
  if (!id) return null
  const date = toIsoDate(row.booking_date)
  return '/bookings?selected_booking=' + id + (date ? '&date=' + date : '')
}

// get-booking-by-id returns { booking_date } ('' when the slot is missing).
// Returns 'YYYY-MM-DD' or null.
export function bookingDateFromLookup(data) {
  return toIsoDate(data && data.booking_date)
}

// Re-adds the deep link to a preferred-view redirect query. getBookings strips
// selected_booking from the URL once it handles it, which can happen before the
// site-settings redirect runs on a first-ever load. The date is the link's own
// (captured at mount): by redirect time the page has written its default date
// into the URL, which would pass for the booking's date and skip the lookup.
export function withDeepLinkBooking(query, bookingId, linkDate) {
  if (!bookingId) return query
  const next = { ...query, selected_booking: String(bookingId) }
  if (linkDate) next.date = linkDate
  else delete next.date
  return next
}

// The card to open for a deep-linked slot id. In split public mode the master
// card isn't rendered, so a child card's masterCard stands in for it.
export function findDeepLinkedCard(cards, bookingId) {
  if (!Array.isArray(cards) || !bookingId) return null
  const id = String(bookingId)
  const direct = cards.find(c => String(c.id) === id)
  if (direct) return direct
  const child = cards.find(c => c.masterCard && String(c.masterCard.id) === id)
  return child ? child.masterCard : null
}
